import { useMemo, useState } from 'react'

import { styled } from 'styled-components'

import type { OrchestratedQuote } from '@b-stock/fe-core/bstock-next/queries'
import {
  Banner,
  CurrencyStringCents,
  FormattedMessage,
} from '@b-stock/fe-core/bstock-react'
import { Colors, Spacing, Typography } from '@b-stock/fe-core/design-system'
import { CommonStrings } from '@b-stock/fe-core/locale'

import { calculateShippingCostBreakdown } from '@/helpers/calculateShippingCostBreakdown'

const Body = styled.div`
  display: grid;
  ${Spacing.Primitive.Mm.css('gap')};
`

const Content = styled.div`
  align-items: start;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
`

const Label = styled.div`
  ${Typography.Subtitle1}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
  white-space: nowrap;
`

const Value = styled.div`
  ${Typography.Body2}
  color: ${Colors.Semantic.Neutral.black};
  white-space: nowrap;
`

enum QuoteStateEnum {
  NoQuoteDueToLackOfInfo = 'noQuoteDueToLackOfInfo',
  NoQuoteDueToAddressError = 'noQuoteDueToAddressError',
  HasQuote = 'hasQuote',
}

export const ShippingQuote = ({
  selectedQuote,
  showQuoteInformation,
  currency,
}: {
  selectedQuote?: OrchestratedQuote | null
  showQuoteInformation: boolean
  currency: string
}) => {
  const [showBanner, setShowBanner] = useState(true)
  const { estimatedShippingAmount } =
    calculateShippingCostBreakdown(selectedQuote)

  const quoteState = useMemo<QuoteStateEnum>(() => {
    if (!showQuoteInformation) {
      return QuoteStateEnum.HasQuote
    }
    if (!selectedQuote) {
      return QuoteStateEnum.NoQuoteDueToAddressError
    }
    if (estimatedShippingAmount === 0) {
      return QuoteStateEnum.NoQuoteDueToLackOfInfo
    }
    return QuoteStateEnum.HasQuote
  }, [showQuoteInformation, estimatedShippingAmount, selectedQuote])

  const bannerType = !showQuoteInformation
    ? 'info'
    : quoteState === QuoteStateEnum.NoQuoteDueToAddressError
      ? 'warning'
      : 'info'

  return (
    <Body>
      {showBanner && (
        <Banner
          type={bannerType}
          {...(bannerType !== 'warning' && {
            onDismiss: () => setShowBanner(false),
          })}
        >
          <FormattedMessage
            id={`Listing.actionColumn.contractPaymentCard.quoteStateBanner.${quoteState}`}
          />
        </Banner>
      )}
      <Content>
        <Label data-testid="estimatedTotalShippingQuote-label">
          <FormattedMessage id="Listing.actionColumn.contractPaymentCard.estimatedTotalShippingQuote" />
        </Label>
        <Value data-testid="estimatedTotalShippingQuote-value">
          {estimatedShippingAmount ? (
            <CurrencyStringCents
              value={estimatedShippingAmount}
              currency={currency}
            />
          ) : (
            <CommonStrings.NotAvailableAbbr.Component />
          )}
        </Value>
      </Content>
    </Body>
  )
}
